import type { Editor } from '@tiptap/react'
import { EditorState, PluginKey } from '@tiptap/pm/state'
import { EditorView } from '@tiptap/pm/view'
import tippy, { Instance, Props } from 'tippy.js'
import 'tippy.js/themes/light.css'
import 'tippy.js/dist/border.css'
import 'tippy.js/dist/svg-arrow.css'

export interface ActionsPluginProps {
  pluginKey: PluginKey | string
  editor: Editor
  element: HTMLElement
  tippyOptions?: Partial<Props>
  updateDelay?: number
  shouldShow?:
    | ((props: {
        editor: Editor
        view: EditorView
        state: EditorState
        oldState?: EditorState
        from: number
        to: number
        hoverTarget?: HTMLElement
      }) => boolean)
    | null
}

type ActionsViewProps = ActionsPluginProps & {
  view: EditorView
}

export class ActionsView {
  public editor: Editor
  public element: HTMLElement
  public view: EditorView
  public preventHide = false
  public tippy: Instance | undefined
  public tippyOptions?: Partial<Props>
  public updateDelay: number
  private updateDebounceTimer: number | undefined
  public shouldShow: Exclude<ActionsPluginProps['shouldShow'], null>
  public hoveredElement: HTMLElement | undefined

  constructor({
    editor,
    element,
    view,
    tippyOptions = {},
    updateDelay = 250,
    shouldShow,
  }: ActionsViewProps) {
    this.editor = editor
    this.element = element
    this.view = view
    this.updateDelay = updateDelay

    if (shouldShow) {
      this.shouldShow = shouldShow
    }

    this.element.addEventListener('mousedown', this.mousedownHandler, {
      capture: true,
    })
    this.view.dom.addEventListener('dragstart', this.dragstartHandler)
    this.editor.on('focus', this.focusHandler)
    this.editor.on('blur', this.blurHandler)
    this.view.dom.addEventListener('mouseover', this.mouseoverHandler)
    this.tippyOptions = tippyOptions
    this.element.remove()
    this.element.style.visibility = 'visible'

    // Add mutation observer to hide when rich text menu appears
    this.setupMutationObserver()
  }

  private mutationObserver: MutationObserver | null = null

  private setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const richTextMenu = document.querySelector(
            '[data-bubble-menu-rich-text="true"]'
          )
          if (richTextMenu) {
            this.hide()
          }
        }
      }
    })

    // Start observing the document with the configured parameters
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  public hoverTimeout: number | undefined

  public mouseoverHandler = (event: MouseEvent) => {
    const { target } = event

    if (!(target instanceof HTMLElement)) return
    if (target === this.hoveredElement) return
    if (this.hoverTimeout) {
      window.clearTimeout(this.hoverTimeout)
    }
    this.hoverTimeout = window.setTimeout(() => {
      this.hoveredElement = target
      this.update(this.editor.view)
    }, 100)
  }

  mousedownHandler = () => {
    this.preventHide = true
  }

  dragstartHandler = () => {
    this.hide()
  }

  focusHandler = () => {
    setTimeout(() => this.update(this.editor.view))
  }

  blurHandler = ({ event }: { event: FocusEvent }) => {
    if (this.preventHide) {
      this.preventHide = false
      return
    }

    if (
      event?.relatedTarget &&
      this.element.parentNode?.contains(event.relatedTarget as Node)
    ) {
      return
    }

    this.hide()
  }

  tippyBlurHandler = (event: FocusEvent) => {
    this.blurHandler({ event })
  }

  createTooltip() {
    const { element: editorElement } = this.editor.options
    const editorIsAttached = !!editorElement.parentElement

    if (this.tippy || !editorIsAttached) {
      return
    }

    this.tippy = tippy(editorElement, {
      duration: 10,
      getReferenceClientRect: null,
      content: this.element,
      interactive: false,
      trigger: 'manual',
      placement: 'top',
      offset: [0, 4],
      delay: [300, 0],
      ...this.tippyOptions,
    })

    if (this.tippy.popper.firstChild) {
      this.tippy.popper.firstChild.addEventListener(
        'blur',
        this.tippyBlurHandler as EventListener
      )
    }
  }

  update(view: EditorView, oldState?: EditorState) {
    const { state } = view
    const hasValidSelection =
      state.selection.$from.pos !== state.selection.$to.pos

    if (this.updateDelay > 0 && hasValidSelection) {
      this.handleDebouncedUpdate(view, oldState)
      return
    }

    const selectionChanged = !oldState?.selection.eq(view.state.selection)
    const docChanged = !oldState?.doc.eq(view.state.doc)

    this.updateHandler(view, selectionChanged, docChanged, oldState)
  }

  handleDebouncedUpdate = (view: EditorView, oldState?: EditorState) => {
    const selectionChanged = !oldState?.selection.eq(view.state.selection)
    const docChanged = !oldState?.doc.eq(view.state.doc)

    if (!selectionChanged && !docChanged) {
      return
    }

    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer)
    }

    this.updateDebounceTimer = window.setTimeout(() => {
      this.updateHandler(view, selectionChanged, docChanged, oldState)
    }, this.updateDelay)
  }

  updateHandler = (
    view: EditorView,
    selectionChanged: boolean,
    docChanged: boolean,
    oldState?: EditorState
  ) => {
    const { state, composing } = view
    const { selection } = state

    const isSame = !selectionChanged && !docChanged

    if (composing || isSame) {
      return
    }

    this.createTooltip()

    const { ranges } = selection
    const from = Math.min(...ranges.map((range) => range.$from.pos))
    const to = Math.max(...ranges.map((range) => range.$to.pos))

    // Check for rich text menu before showing
    const richTextMenu = document.querySelector(
      '[data-bubble-menu-rich-text="true"]'
    )
    if (richTextMenu) {
      this.hide()
      return
    }

    const shouldShow = this.shouldShow?.({
      editor: this.editor,
      view,
      state,
      oldState,
      from,
      to,
    })

    if (!shouldShow) {
      this.hide()
      return
    }

    this.tippy?.setProps({
      getReferenceClientRect:
        this.tippyOptions?.getReferenceClientRect ||
        (() => {
          return this.hoveredElement!.getBoundingClientRect()
        }),
    })

    this.show()
  }

  show() {
    this.tippy?.show()
  }

  hide() {
    this.tippy?.hide()
  }

  destroy() {
    if (this.tippy?.popper.firstChild) {
      this.tippy.popper.firstChild.removeEventListener(
        'blur',
        this.tippyBlurHandler as EventListener
      )
    }
    this.tippy?.destroy()
    this.element.removeEventListener('mousedown', this.mousedownHandler, {
      capture: true,
    })
    this.view.dom.removeEventListener('dragstart', this.dragstartHandler)
    this.editor.off('focus', this.focusHandler)
    this.editor.off('blur', this.blurHandler)
    this.mutationObserver?.disconnect()
  }
}
